import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from './ui/card';
import { Button } from './ui/button';
import {
  ChevronDown,
  ChevronRight,
  Edit,
  Save,
  Plus,
  Trash2,
  Check,
  X,
  RefreshCw,
} from 'lucide-react';
import { getAllRoles, createRole, updateRolePermissions, deleteRole } from '../api';
import { sidebarItems } from './sidebarConfig';
import { useAuth } from '../contexts/AuthContext'; // Importar el contexto de autenticación

const RoleManagement = () => {
  const { updatePermissionsConfig } = useAuth(); // Usar el contexto de autenticación
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [expandedRoles, setExpandedRoles] = useState({});
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState({});
  const [editedPermissions, setEditedPermissions] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  // Extraer páginas disponibles del sidebar y App.js
  useEffect(() => {
    // Extraer páginas del sidebar
    const extractPagesFromSidebar = () => {
      return sidebarItems.map(item => ({
        id: item.path.replace('/', ''), // Usamos la ruta sin la barra como ID
        name: item.title,
        path: item.path,
        icon: item.icon,
        allowedRoles: item.roles || []
      }));
    };
    
    setPages(extractPagesFromSidebar());
  }, []);

  // Cargar roles desde la API
  const loadRoles = async () => {
    setLoading(true);
    try {
      // Intentar cargar roles desde la API
      const rolesData = await getAllRoles();
      
      // Si la API devuelve datos, usarlos
      if (rolesData && Array.isArray(rolesData)) {
        setRoles(rolesData);
        
        // Inicializar los estados expandidos
        const initialExpandedState = {};
        rolesData.forEach(role => {
          initialExpandedState[role.id] = false;
        });
        setExpandedRoles(initialExpandedState);

        // Actualizar la caché de roles en localStorage
        updateRolesCache(rolesData);
      } else {
        // Si no hay datos o la API falla, mostrar un mensaje
        toast.warning('No se pudieron cargar los roles desde la API, mostrando datos de ejemplo');
        
        // Usar datos de ejemplo como fallback
        const fallbackRoles = [
          { id: 1, name: 'admin', permissions: ['dashboard', 'settings', 'orders', 'shipments', 'inbound', 'transportAdmin'] },
          { id: 2, name: 'user', permissions: ['dashboard', 'orders'] },
          { id: 12, name: 'transportista', permissions: ['transportista-dashboard'] },
          // Otros roles de ejemplo...
        ];
        
        setRoles(fallbackRoles);
        
        // Inicializar los estados expandidos para los datos de fallback
        const initialExpandedState = {};
        fallbackRoles.forEach(role => {
          initialExpandedState[role.id] = false;
        });
        setExpandedRoles(initialExpandedState);

        // Actualizar la caché de roles incluso con los datos de fallback
        updateRolesCache(fallbackRoles);
      }
    } catch (error) {
      console.error('Error al cargar los roles:', error);
      toast.error('Error al cargar los roles');
      
      // En caso de error, usar datos de ejemplo como fallback
      const fallbackRoles = [
        { id: 1, name: 'admin', permissions: ['dashboard', 'settings', 'orders', 'shipments', 'inbound', 'transportAdmin'] },
        { id: 2, name: 'user', permissions: ['dashboard', 'orders'] },
        { id: 12, name: 'transportista', permissions: ['transportista-dashboard'] },
        { id: 3, name: 'client', permissions: ['dashboard'] },
        { id: 4, name: 'Trafico', permissions: ['dashboard', 'orders', 'shipments'] },
        { id: 5, name: 'Documental', permissions: ['dashboard', 'inbound'] },
        { id: 6, name: 'Procurement', permissions: ['dashboard', 'orders', 'inbound'] },
        { id: 7, name: 'ControlTower', permissions: ['dashboard', 'active-shipments'] },
        { id: 8, name: 'Sourcing', permissions: ['dashboard', 'transport-admin'] },
        { id: 9, name: 'Multicuentas', permissions: ['dashboard', 'orders', 'shipments'] },
        { id: 10, name: 'Legal', permissions: ['dashboard', 'tickets'] },
        { id: 11, name: 'Propios', permissions: ['dashboard', 'orders', 'shipments', 'anticipos', 'gas-vouchers'] },
      ];
      
      setRoles(fallbackRoles);
      
      // Inicializar los estados expandidos para los datos de fallback
      const initialExpandedState = {};
      fallbackRoles.forEach(role => {
        initialExpandedState[role.id] = false;
      });
      setExpandedRoles(initialExpandedState);

      // Actualizar la caché de roles incluso con los datos de fallback
      updateRolesCache(fallbackRoles);
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar la caché de roles en localStorage
  const updateRolesCache = (rolesData) => {
    try {
      // Convertir el array de roles a un objeto mapeado por nombre de rol
      const rolesMap = {};
      rolesData.forEach(role => {
        rolesMap[role.name] = role.permissions || [];
      });
      
      // Guardar en localStorage
      localStorage.setItem('rolesConfig', JSON.stringify(rolesMap));
      console.log('Caché de roles actualizada en localStorage:', rolesMap);
    } catch (error) {
      console.error('Error al actualizar la caché de roles:', error);
    }
  };

  // Cargar roles al montar el componente
  useEffect(() => {
    loadRoles();
  }, []);

  // Toggle expandir/colapsar rol
  const toggleRoleExpansion = (roleId) => {
    setExpandedRoles(prev => ({
      ...prev,
      [roleId]: !prev[roleId]
    }));
  };

  // Iniciar edición de permisos de rol
  const startEditingRole = (role) => {
    setEditingRoleId(role.id);
    setEditedPermissions({
      ...editedPermissions,
      [role.id]: [...role.permissions]
    });
  };

  // Guardar permisos editados
  const saveRolePermissions = async (roleId) => {
    setLoading(true);
    try {
      // Guardar en la API
      await updateRolePermissions(roleId, editedPermissions[roleId]);
      
      // Actualizar estado localmente
      const updatedRoles = roles.map(role => {
        if (role.id === roleId) {
          return { ...role, permissions: editedPermissions[roleId] };
        }
        return role;
      });
      
      setRoles(updatedRoles);

      // Actualizar la caché de roles en localStorage
      updateRolesCache(updatedRoles);

      // Actualizar los permisos en el contexto de autenticación
      // Hacemos dos llamadas - una inmediata y otra con delay para asegurar actualización
      await updatePermissionsConfig();
      
      // Segunda actualización después de un breve delay para asegurar que los cambios se reflejen
      setTimeout(async () => {
        await updatePermissionsConfig();
        console.log('Segunda actualización de permisos completada');
      }, 1000);

      toast.success('Permisos actualizados correctamente');
      setEditingRoleId(null);
      
      toast.info('Los permisos han sido actualizados. Los cambios se reflejarán inmediatamente.');
    } catch (error) {
      console.error('Error al actualizar permisos:', error);
      toast.error('Error al actualizar permisos');
    } finally {
      setLoading(false);
    }
  };

  // Cancelar edición
  const cancelEditing = () => {
    setEditingRoleId(null);
  };

  // Cambiar permiso durante edición
  const togglePermission = (roleId, pageId) => {
    setEditedPermissions(prev => {
      const permissions = prev[roleId] || [];
      
      if (permissions.includes(pageId)) {
        return {
          ...prev,
          [roleId]: permissions.filter(id => id !== pageId)
        };
      } else {
        return {
          ...prev,
          [roleId]: [...permissions, pageId]
        };
      }
    });
  };

  // Iniciar creación de rol
  const startCreatingRole = () => {
    setIsCreatingRole(true);
    setNewRoleName('');
    setNewRolePermissions({});
  };

  // Guardar nuevo rol
  const saveNewRole = async () => {
    if (!newRoleName.trim()) {
      toast.error('El nombre del rol no puede estar vacío');
      return;
    }

    setLoading(true);
    try {
      // Obtener los permisos seleccionados
      const selectedPermissions = Object.keys(newRolePermissions).filter(key => newRolePermissions[key]);
      
      // Crear rol en la API
      const createdRole = await createRole(newRoleName, selectedPermissions);
      
      // Si la API devuelve el rol creado, usarlo
      let updatedRoles;
      if (createdRole) {
        updatedRoles = [...roles, createdRole];
        setRoles(updatedRoles);
        setExpandedRoles({
          ...expandedRoles,
          [createdRole.id]: false
        });
      } else {
        // Si no, crear un rol simulado para la UI
        const newRole = {
          id: roles.length + 1,
          name: newRoleName,
          permissions: selectedPermissions
        };
        
        updatedRoles = [...roles, newRole];
        setRoles(updatedRoles);
        setExpandedRoles({
          ...expandedRoles,
          [newRole.id]: false
        });
      }

      // Actualizar la caché de roles en localStorage
      updateRolesCache(updatedRoles);

      // Actualizar permisos en el contexto de autenticación
      await updatePermissionsConfig();

      toast.success('Rol creado correctamente');
      setIsCreatingRole(false);
      
      toast.info('Los permisos han sido actualizados. Los cambios se reflejarán inmediatamente.');
    } catch (error) {
      console.error('Error al crear rol:', error);
      toast.error('Error al crear rol');
    } finally {
      setLoading(false);
    }
  };

  // Eliminar rol
  const handleDeleteRole = async (roleId) => {
    if (roleId === 1) {  // Evitar eliminar el rol admin
      toast.error('No se puede eliminar el rol admin');
      return;
    }

    if (window.confirm('¿Estás seguro de que deseas eliminar este rol?')) {
      setLoading(true);
      try {
        // Eliminar rol en la API
        await deleteRole(roleId);
        
        // Actualizar estado localmente
        const updatedRoles = roles.filter(role => role.id !== roleId);
        setRoles(updatedRoles);
        
        // Actualizar la caché de roles en localStorage
        updateRolesCache(updatedRoles);
        
        // Actualizar permisos en el contexto de autenticación
        await updatePermissionsConfig();
        
        toast.success('Rol eliminado correctamente');
        
        toast.info('Los permisos han sido actualizados. Los cambios se reflejarán inmediatamente.');
      } catch (error) {
        console.error('Error al eliminar rol:', error);
        toast.error('Error al eliminar rol');
      } finally {
        setLoading(false);
      }
    }
  };

  // Cambiar permiso durante creación de rol
  const toggleNewRolePermission = (pageId) => {
    setNewRolePermissions(prev => ({
      ...prev,
      [pageId]: !prev[pageId]
    }));
  };

  // Obtener nombre de página a partir de su ID
  const getPageNameById = (pageId) => {
    const page = pages.find(p => p.id === pageId || p.path === `/${pageId}`);
    return page ? page.name : pageId;
  };

  // Función para actualizar manualmente permisos desde la API
  const handleRefreshPermissions = async () => {
    setRefreshing(true);
    try {
      // Recargar los roles
      await loadRoles();
      
      // Actualizar permisos en el contexto
      await updatePermissionsConfig();
      
      toast.success('Permisos actualizados correctamente');
    } catch (error) {
      console.error('Error al actualizar permisos:', error);
      toast.error('Error al actualizar permisos');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Card className="shadow-lg hover:shadow-2xl transition-all duration-300 mt-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium">Gestión de Roles y Permisos</CardTitle>
        <div className="flex gap-2">
          <Button 
            onClick={handleRefreshPermissions}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading || refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> 
            Actualizar
          </Button>
          <Button 
            onClick={startCreatingRole}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            disabled={loading || isCreatingRole || refreshing}
          >
            <Plus className="h-4 w-4 mr-2" /> Nuevo Rol
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center p-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {isCreatingRole && (
              <Card className="mb-4 p-4 border-2 border-blue-200">
                <div className="flex flex-col space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre del Rol</label>
                    <input
                      type="text"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                      placeholder="Ingrese el nombre del rol"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permisos</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {pages.map(page => (
                        <div key={page.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`new-${page.id}`}
                            checked={!!newRolePermissions[page.id]}
                            onChange={() => toggleNewRolePermission(page.id)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600"
                          />
                          <label htmlFor={`new-${page.id}`} className="ml-2 text-sm text-gray-700">
                            {page.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-2 mt-4">
                    <Button
                      onClick={() => setIsCreatingRole(false)}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={saveNewRole}
                      className="bg-green-500 hover:bg-green-600 text-white"
                      disabled={!newRoleName.trim()}
                    >
                      Guardar Rol
                    </Button>
                  </div>
                </div>
              </Card>
            )}
            
            {roles.map(role => (
              <div key={role.id} className="mb-4">
                <div 
                  className="flex items-center justify-between bg-gray-100 p-3 rounded-t-md cursor-pointer"
                  onClick={() => toggleRoleExpansion(role.id)}
                >
                  <div className="flex items-center">
                    {expandedRoles[role.id] ? (
                      <ChevronDown className="h-5 w-5 mr-2" />
                    ) : (
                      <ChevronRight className="h-5 w-5 mr-2" />
                    )}
                    <span className="font-medium">{role.name}</span>
                  </div>
                  <div className="flex space-x-2">
                    {editingRoleId === role.id ? (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); saveRolePermissions(role.id); }}
                          className="p-1 rounded-full bg-green-100 hover:bg-green-200"
                          title="Guardar"
                        >
                          <Save className="h-4 w-4 text-green-600" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); cancelEditing(); }}
                          className="p-1 rounded-full bg-red-100 hover:bg-red-200"
                          title="Cancelar"
                        >
                          <X className="h-4 w-4 text-red-600" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); startEditingRole(role); }}
                          className="p-1 rounded-full bg-blue-100 hover:bg-blue-200"
                          title="Editar permisos"
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </button>
                        {role.id !== 1 && ( // Evitar eliminar el rol admin
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteRole(role.id); }}
                            className="p-1 rounded-full bg-red-100 hover:bg-red-200"
                            title="Eliminar rol"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {expandedRoles[role.id] && (
                  <div className="border border-gray-200 rounded-b-md p-3 bg-white">
                    <h3 className="text-sm font-medium mb-2">Permisos:</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                      {pages.map(page => {
                        const isEditing = editingRoleId === role.id;
                        const permissions = isEditing ? (editedPermissions[role.id] || []) : role.permissions;
                        const hasPermission = permissions.includes(page.id);
                        
                        return (
                          <div key={page.id} className="flex items-center">
                            {isEditing ? (
                              <input
                                type="checkbox"
                                id={`${role.id}-${page.id}`}
                                checked={hasPermission}
                                onChange={() => togglePermission(role.id, page.id)}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600"
                              />
                            ) : (
                              <div className={`flex-shrink-0 h-4 w-4 flex items-center justify-center rounded 
                                ${hasPermission ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}
                              >
                                {hasPermission ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                              </div>
                            )}
                            <label 
                              htmlFor={`${role.id}-${page.id}`} 
                              className={`ml-2 text-sm ${hasPermission ? 'text-gray-900' : 'text-gray-500'}`}
                            >
                              {page.name}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RoleManagement;